import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { getIndustry } from "../../data/industries";
import { getSolution } from "../../data/solutions";
import NotFound from "./NotFound";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  if (!industry) return <NotFound />;

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid={`industry-detail-${slug}`}>
      <SiteHeader />
      <PageHero crumb="Industries" eyebrow="Industry" title={industry.name} description={industry.intro} />
      <section className="py-16 sm:py-24">
        <div className="nx-wrap">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <Reveal>
                <h2 className="font-marcellus text-2xl sm:text-3xl text-white mb-8">The challenges</h2>
              </Reveal>
              <Stagger className="flex flex-col gap-px bg-white/10 border border-white/10">
                {industry.challenges.map((c, i) => (
                  <StaggerItem key={c}>
                    <div className="flex items-start gap-5 bg-nx-navy px-6 py-5" data-testid={`industry-challenge-${i}`}>
                      <span className="font-marcellus text-nx-gold/70 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm sm:text-base text-slate-200 leading-relaxed">{c}</span>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
            <div>
              <Reveal>
                <h2 className="font-marcellus text-2xl sm:text-3xl text-white mb-8">The NexSecure approach</h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed" data-testid="industry-approach">{industry.approach}</p>
              </Reveal>
              <Reveal delay={0.2}>
                <h3 className="text-[11px] tracking-[0.28em] uppercase text-nx-goldlight mt-12 mb-5">Solutions we bring</h3>
                <div className="flex flex-col gap-3">
                  {industry.solutions.map((ss) => {
                    const sol = getSolution(ss);
                    return sol ? (
                      <Link key={ss} to={`/solutions/${ss}`} className="group flex items-center justify-between border border-white/10 hover:border-nx-gold/50 px-5 py-3.5 transition-colors" data-testid={`industry-solution-${ss}`}>
                        <span className="text-sm text-slate-200">{sol.name}</span>
                        <ArrowRight size={14} className="text-slate-500 group-hover:text-nx-goldlight group-hover:translate-x-1 transition-all" />
                      </Link>
                    ) : null;
                  })}
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.15} className="mt-14">
            <Link to="/industries" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors" data-testid="industry-back-link">
              <ArrowLeft size={14} /> All industries
            </Link>
          </Reveal>
        </div>
      </section>
      <CTABand title={`Running a ${industry.name.toLowerCase().replace(/&/g, "and")} project?`} />
      <SiteFooter />
    </div>
  );
}
